import React, { useState, useEffect, useMemo } from 'react';
import smartScheduleService from '../services/smartScheduleService';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';

const AvailableSlotsModal = ({ isOpen, onClose, firstContact, onScheduled }) => {
  const [view, setView] = useState('loading'); // 'loading', 'returning', 'new_indication'
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [error, setError] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [createSurvey, setCreateSurvey] = useState(true);
  const [displayedSlotsCount, setDisplayedSlotsCount] = useState(5);

  // Estados para o calendário de retorno
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [dateSlots, setDateSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const isReturningClient = firstContact?.origin_type === 'ANTIGO' || firstContact?.is_second_work;

  useEffect(() => {
    if (isOpen && firstContact?.id) {
      checkClientType();
    }
  }, [isOpen, firstContact]);

  const checkClientType = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isReturningClient) {
        // Cliente retorno: buscar datas disponíveis do calendário
        const response = await smartScheduleService.getReturningAvailableDates({
          first_contact_id: firstContact.id,
          month: currentMonth.getMonth() + 1,
          year: currentMonth.getFullYear()
        });

        if (response.success) {
          setAvailableDates(response.data.available_dates || []);
          setClientInfo({
            client_name: firstContact.client_name,
            origin_type: firstContact.origin_type,
            is_returning_client: true
          });
          setView('returning');
        } else {
          setError(response.message || 'Erro ao buscar datas disponíveis');
        }
      } else {
        // Cliente novo ou indicação: usar lógica completa com score
        const response = await smartScheduleService.findBestSlot({
          first_contact_id: firstContact.id
        });

        if (response.success) {
          setSlots(response.data.recommended_slots || []);
          setClientInfo(response.data.client_info);
          setView('new_indication');
        } else {
          setError(response.message || 'Erro ao buscar horários');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao buscar horários disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = async () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newMonth);

    if (isReturningClient) {
      setLoading(true);
      try {
        const response = await smartScheduleService.getReturningAvailableDates({
          first_contact_id: firstContact.id,
          month: newMonth.getMonth() + 1,
          year: newMonth.getFullYear()
        });
        if (response.success) {
          setAvailableDates(response.data.available_dates || []);
        }
      } catch (err) {
        console.error('Erro ao buscar datas:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNextMonth = async () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newMonth);

    if (isReturningClient) {
      setLoading(true);
      try {
        const response = await smartScheduleService.getReturningAvailableDates({
          first_contact_id: firstContact.id,
          month: newMonth.getMonth() + 1,
          year: newMonth.getFullYear()
        });
        if (response.success) {
          setAvailableDates(response.data.available_dates || []);
        }
      } catch (err) {
        console.error('Erro ao buscar datas:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDateClick = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setLoadingSlots(true);

    try {
      const response = await smartScheduleService.getReturningSlotsByDate({
        first_contact_id: firstContact.id,
        date: date
      });

      if (response.success) {
        setDateSlots(response.data.slots || []);
      } else {
        setError(response.message || 'Erro ao buscar horários');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao buscar horários');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot({
      slot_date: selectedDate,
      slot_time: slot.slot_time,
      duration_minutes: slot.duration_minutes
    });
  };

  const handleSchedule = async () => {
    if (!selectedSlot) return;

    setScheduling(true);
    try {
      const response = await smartScheduleService.scheduleLevantamento({
        first_contact_id: firstContact.id,
        slot_date: selectedSlot.slot_date,
        slot_time: selectedSlot.slot_time,
        duration_minutes: selectedSlot.duration_minutes,
        create_survey: createSurvey
      });

      if (response.success) {
        onScheduled(response.data);
        onClose();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao agendar');
    } finally {
      setScheduling(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      ALTO: { bg: 'bg-red-500', text: 'text-white', label: 'Alta Prioridade' },
      MEDIO: { bg: 'bg-yellow-500', text: 'text-white', label: 'Média Prioridade' },
      BAIXO: { bg: 'bg-green-500', text: 'text-white', label: 'Baixa Prioridade' },
      RETORNO: { bg: 'bg-purple-500', text: 'text-white', label: 'Retorno' }
    };
    return badges[priority] || badges.BAIXO;
  };

  // Renderizar calendário para clientes retorno
  const renderReturningCalendar = () => {
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const today = new Date().toISOString().split('T')[0];

    const days = [];
    // Preencher dias vazios no início
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-16"></div>);
    }

    // Dias do mês
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isAvailable = availableDates.includes(dateStr);
      const isToday = dateStr === today;
      const isSelected = dateStr === selectedDate;

      days.push(
        <button
          key={day}
          onClick={() => isAvailable && handleDateClick(dateStr)}
          disabled={!isAvailable}
          className={`
            p-2 h-16 rounded-lg border-2 transition relative
            ${isAvailable
              ? isSelected
                ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              : 'border-transparent text-gray-300 cursor-not-allowed'
            }
            ${isToday ? 'font-bold' : ''}
          `}
        >
          <span className="text-sm">{day}</span>
          {isAvailable && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>
          )}
        </button>
      );
    }

    return days;
  };

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              📅 {isReturningClient ? 'Selecionar Data e Horário' : 'Horários Recomendados'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Client Info */}
          {clientInfo && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-3">
                <div className="font-medium text-lg">{clientInfo.client_name}</div>
                {!isReturningClient && clientInfo.priority_score && (
                  <span className="text-sm text-gray-500">
                    Prioridade: {clientInfo.priority_score} pts
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Origem: {clientInfo.origin_type === 'INDICACAO' ? '⭐ Indicação' :
                         clientInfo.origin_type === 'ANTIGO' ? '🔄 Cliente Antigo' : '🆕 Novo'}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Carregando...</p>
            </div>
          )}

          {/* View para Cliente NOVO/INDICAÇÃO - Lista de Recomendações */}
          {!loading && view === 'new_indication' && (
            <>
              <div className="mb-4">
                <h3 className="font-medium text-gray-700 mb-2">Horários Recomendados:</h3>
                <div className="space-y-2">
                  {slots.slice(0, displayedSlotsCount).map((slot, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                        selectedSlot?.slot_date === slot.slot_date &&
                        selectedSlot?.slot_time === slot.slot_time
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            📅 {new Date(slot.slot_date + 'T12:00:00').toLocaleDateString('pt-BR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long'
                            })}
                          </div>
                          <div className="text-gray-600">
                            🕐 {slot.slot_time} - {slot.slot_end_time} ({slot.duration_minutes} min)
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs ${getPriorityBadge(slot.priority_match).bg} ${getPriorityBadge(slot.priority_match).text}`}>
                            {getPriorityBadge(slot.priority_match).label}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">{slot.reason}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Botão Ver Mais Opções */}
                {slots.length > displayedSlotsCount && (
                  <button
                    onClick={() => setDisplayedSlotsCount(prev => Math.min(prev + 5, slots.length))}
                    className="mt-3 w-full py-2 px-4 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition flex items-center justify-center gap-2"
                  >
                    <span>+ Ver mais opções ({slots.length - displayedSlotsCount} restantes)</span>
                  </button>
                )}

                {displayedSlotsCount > 5 && (
                  <button
                    onClick={() => setDisplayedSlotsCount(5)}
                    className="mt-2 w-full py-2 px-4 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2"
                  >
                    <span>🔼 Recolher</span>
                  </button>
                )}
              </div>

              {/* Options */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createSurvey}
                    onChange={(e) => setCreateSurvey(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Criar Levantamento (Survey) automaticamente
                  </span>
                </label>
              </div>
            </>
          )}

          {/* View para Cliente RETORNO - Calendário */}
          {!loading && view === 'returning' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Calendário */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePrevMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h3 className="font-medium text-gray-800">
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h3>
                    <button
                      onClick={handleNextMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Dias da semana */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {dayNames.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Dias do mês */}
                  <div className="grid grid-cols-7 gap-1">
                    {renderReturningCalendar()}
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Dias disponíveis conforme configuração de agenda
                  </p>
                </div>

                {/* Horários do dia selecionado */}
                <div>
                  {selectedDate ? (
                    <>
                      <h3 className="font-medium text-gray-800 mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </h3>

                      {loadingSlots ? (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                      ) : dateSlots.length > 0 ? (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {dateSlots.map((slot, index) => (
                            <button
                              key={index}
                              onClick={() => handleSlotSelect(slot)}
                              className={`w-full p-3 rounded-lg border-2 text-left transition ${
                                selectedSlot?.slot_time === slot.slot_time
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <span className="font-medium">
                                  {slot.slot_time} - {slot.slot_end_time}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>Nenhum horário disponível para este dia</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500 h-full flex items-center justify-center">
                      <div>
                        <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p>Selecione uma data disponível</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Options */}
              {selectedSlot && (
                <div className="mb-4 mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createSurvey}
                      onChange={(e) => setCreateSurvey(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Criar Levantamento (Survey) automaticamente
                    </span>
                  </label>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!loading && view === 'new_indication' && slots.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhum horário disponível encontrado.</p>
              <p className="text-sm mt-1">Tente configurar mais dias disponíveis na agenda.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSchedule}
              disabled={!selectedSlot || scheduling}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {scheduling ? 'Agendando...' : 'Confirmar Agendamento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailableSlotsModal;
